/**
 * ActiveCallIndicator - Floating indicator shown during ALL active calls
 * 
 * This replaces the old CallStatusIndicator that was only in the Header.
 * Shows all call states: Initiating, Ringing, Connected (with timer), etc.
 * DRAGGABLE: Users can move this indicator around the screen.
 */

import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { CallContext, CallStatus } from '../CallContext';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import './ActiveCallIndicator.css';

function ActiveCallIndicator() {
    const { currentCall, endCall, showIncomingCall } = useContext(CallContext);
    const [duration, setDuration] = useState(0);
    const [isEnding, setIsEnding] = useState(false);

    // Drag state
    const [position, setPosition] = useState({ x: 20, y: 20 }); // Initial position (top-right)
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    // Timer for active calls (ACCEPTED or CONNECTED)
    useEffect(() => {
        const isCallActive = currentCall &&
            (currentCall.status === CallStatus.ACCEPTED || currentCall.status === CallStatus.CONNECTED);

        if (!isCallActive) {
            setDuration(0);
            return;
        }

        // Calculate initial duration from answered_at if available
        let initialDuration = 0;
        if (currentCall.answered_at) {
            const answeredTime = new Date(currentCall.answered_at);
            initialDuration = Math.floor((Date.now() - answeredTime.getTime()) / 1000);
            if (initialDuration < 0) initialDuration = 0;
        }
        setDuration(initialDuration);

        const timer = setInterval(() => {
            setDuration(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [currentCall?.status, currentCall?.answered_at]);

    // Drag handlers
    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('.active-call-end-btn')) return; // Don't drag when clicking end button

        setIsDragging(true);
        const rect = dragRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        const newX = window.innerWidth - e.clientX - (dragRef.current.offsetWidth - dragOffset.current.x);
        const newY = e.clientY - dragOffset.current.y;

        // Keep within viewport bounds
        const maxX = window.innerWidth - dragRef.current.offsetWidth - 10;
        const maxY = window.innerHeight - dragRef.current.offsetHeight - 10;

        setPosition({
            x: Math.max(10, Math.min(newX, maxX)),
            y: Math.max(10, Math.min(newY, maxY))
        });
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Touch handlers for mobile
    const handleTouchStart = useCallback((e) => {
        if (e.target.closest('.active-call-end-btn')) return;

        const touch = e.touches[0];
        setIsDragging(true);
        const rect = dragRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        };
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!isDragging) return;

        const touch = e.touches[0];
        const newX = window.innerWidth - touch.clientX - (dragRef.current.offsetWidth - dragOffset.current.x);
        const newY = touch.clientY - dragOffset.current.y;

        const maxX = window.innerWidth - dragRef.current.offsetWidth - 10;
        const maxY = window.innerHeight - dragRef.current.offsetHeight - 10;

        setPosition({
            x: Math.max(10, Math.min(newX, maxX)),
            y: Math.max(10, Math.min(newY, maxY))
        });
    }, [isDragging]);

    // Add/remove event listeners
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    const handleEndCall = async () => {
        if (!currentCall || isEnding) return;

        setIsEnding(true);
        try {
            await endCall(currentCall.call_id);
        } finally {
            setIsEnding(false);
        }
    };

    // Don't show if incoming call modal is visible (for inbound calls)
    if (showIncomingCall && currentCall?.direction === 'inbound') {
        return null;
    }

    // Only show for active calls
    if (!currentCall || !currentCall.is_active) {
        return null;
    }

    // Format duration as MM:SS
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Status text and color - show for ALL statuses
    const getStatusInfo = () => {
        switch (currentCall.status) {
            case CallStatus.INITIATED:
                return { text: 'Initiating...', color: '#f0ad4e', pulsing: true };
            case CallStatus.RINGING:
                return { text: 'Ringing...', color: '#5bc0de', pulsing: true };
            case CallStatus.ACCEPTED:
            case CallStatus.CONNECTED:
                return { text: formatDuration(duration), color: '#25D366', pulsing: false };
            default:
                return { text: currentCall.status, color: '#999', pulsing: true };
        }
    };

    // Format phone number
    const formatPhone = (phone) => {
        if (!phone) return 'Unknown';
        if (phone.length > 10) {
            return `+${phone.slice(0, 2)} ${phone.slice(2)}`;
        }
        return phone;
    };

    const statusInfo = getStatusInfo();
    const otherParty = currentCall.direction === 'inbound'
        ? currentCall.caller_phone
        : currentCall.recipient_phone;

    return (
        <div
            ref={dragRef}
            className={`active-call-indicator ${isDragging ? 'dragging' : ''} ${statusInfo.pulsing ? 'pulsing-border' : ''}`}
            style={{ right: position.x, top: position.y }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div className="active-call-content">
                <div className="drag-handle" title="Drag to move">
                    <DragIndicatorIcon />
                </div>
                <div className={`active-call-pulse ${statusInfo.pulsing ? 'fast' : ''}`}></div>
                <PhoneInTalkIcon className={`active-call-icon ${statusInfo.pulsing ? 'pulsing' : ''}`} />
                <div className="active-call-info">
                    <div className="active-call-phone">{formatPhone(otherParty)}</div>
                    <div
                        className="active-call-status"
                        style={{ color: statusInfo.color }}
                    >
                        {statusInfo.text}
                    </div>
                </div>
                <button
                    className="active-call-end-btn"
                    onClick={handleEndCall}
                    disabled={isEnding}
                    title="End Call"
                >
                    <CallEndIcon />
                </button>
            </div>
        </div>
    );
}

export default ActiveCallIndicator;
